// src/lib/blog/config/authors.ts
//
// Author profiles live in their OWN module (split out of `index.ts`). Author
// archive pages ARE prerendered (`generateStaticParams` + `revalidate`), so each
// one's sitemap `<lastmod>` tracks its actual rendered-HTML fingerprint
// (`scripts/build-content-fingerprints.ts` → `content-fingerprints.json`) — an
// edit to an author's bio/avatar/role re-renders only that author's page, bumping
// it per-route, never the whole blog. `blogSitemapProvider` resolves an author
// archive as `max(contentFingerprint, freshest(members))`. See
// `docs/seo/sitemap-lastmod-architecture.md`.

import type { Author } from '~/lib/blog/types/blog.types'

/**
 * Blog authors configuration
 */
export const AUTHORS: Author[] = [
  {
    name: 'Morgan Titcher',
    role: 'Co-Founder & CEO',
    href: '/blog/authors/morgan-titcher',
    imageUrl: '/images/blog/avatars/team/adsights-team-morgan-titcher-avatar-themed-background-foreground.webp',
    bio: 'Founder of AdSights. Building tools to help advertisers make better creative decisions.',
    socialLinks: {
      twitter: 'https://twitter.com/morgantitcher',
      linkedin: 'https://linkedin.com/in/morgantitcher'
    },
    routeMetadata: {
      path: '/blog/authors/morgan-titcher',
      priority: 0.2,
      changefreq: 'monthly'
    }
  },
  {
    name: 'AdSights Team',
    role: 'AI-powered marketing tools',
    href: '/blog/authors/adsights-team',
    imageUrl: '/logo-square_80x80.png',
    bio: 'The AdSights team is a group of AI experts, developers, and marketing professionals who are passionate about helping advertisers make better creative decisions.',
    routeMetadata: {
      path: '/blog/authors/adsights-team',
      priority: 0.5,
      changefreq: 'weekly'
    }
  },
  {
    name: 'Emily Luther',
    role: 'Head of Product',
    href: '/blog/authors/emily-luther',
    imageUrl: '/images/blog/avatars/team/adsights-team-emily-luther-avatar-themed-background-foreground.webp',
    bio: 'Product leader with expertise in AI and analytics platforms.',
    socialLinks: {
      linkedin: 'https://www.linkedin.com/in/emily-luther'
    },
    routeMetadata: {
      path: '/blog/authors/emily-luther',
      priority: 0.2,
      changefreq: 'monthly'
    }
  },
  {
    name: 'Fabrice Armisen',
    role: 'Co-Founder & CTO',
    href: '/blog/authors/fabrice-armisen',
    imageUrl: '/images/blog/avatars/team/adsights-team-fabrice-armisen-avatar-themed-background-foreground.webp',
    bio: 'Creative director with 10+ years in digital advertising.',
    socialLinks: {
      linkedin: 'https://www.linkedin.com/in/farmisen/'
    },
    routeMetadata: {
      path: '/blog/authors/fabrice-armisen',
      priority: 0.2,
      changefreq: 'monthly'
    }
  }
]

/**
 * Canonical URL slug for an author — the last path segment of their `href`.
 * This is the exact value `generateStaticParams` prerenders, so every surface
 * (page, metadata, OG card, validation, post filtering) must resolve authors
 * by it identically. A substring/`includes` match lets a non-canonical slug
 * (e.g. `team` ⊂ `adsights-team`) resolve on one surface but not another.
 */
export function authorSlugFromHref(href: string): string {
  return href.split('/').pop() ?? ''
}

/**
 * Resolve an author by their canonical URL slug (exact, not substring).
 *
 * @param {string} slug - The `[slug]` route segment.
 * @returns {Author | undefined} The matching author, or `undefined`.
 */
export function findAuthorBySlug(slug: string): Author | undefined {
  return AUTHORS.find((author) => authorSlugFromHref(author.href) === slug)
}
